import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiClipboard,
  FiMail,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUserPlus,
  FiXCircle,
} from "react-icons/fi";

import {
  getNewsletterSubscribers,
  toggleNewsletterSubscriberImported,
  deleteNewsletterSubscriber,
} from "../../../services/newsletterSubscriberServices";

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
};

const getToken = () => {
  const user = getStoredUser();
  return user?.token || localStorage.getItem("token") || "";
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalize = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export default function NewsletterSubscribersSection() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState("");
  const [error, setError] = useState("");
  const [isDemoMode, setIsDemoMode] = useState(false);

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending"); // pending | imported | all
  const [copiedId, setCopiedId] = useState(null);

  const checkUserRole = () => {
    const user = getStoredUser();

    if (user?.role_id === 4) {
      setIsDemoMode(true);
    }
  };

  const loadSubscribers = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();
      const data = await getNewsletterSubscribers({}, token);

      setSubscribers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur récupération emails newsletter :", err);
      setError("Impossible de charger les emails inscrits.");
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUserRole();
    loadSubscribers();
  }, []);

  const stats = useMemo(() => {
    const total = subscribers.length;
    const imported = subscribers.filter((s) => s.is_imported_to_contacts).length;
    const pending = total - imported;

    return {
      total,
      imported,
      pending,
    };
  }, [subscribers]);

  const filteredSubscribers = useMemo(() => {
    let rows = [...subscribers];

    if (statusFilter === "pending") {
      rows = rows.filter((s) => !s.is_imported_to_contacts);
    }

    if (statusFilter === "imported") {
      rows = rows.filter((s) => s.is_imported_to_contacts);
    }

    const nq = normalize(q);

    if (nq) {
      rows = rows.filter((s) => normalize(s.email).includes(nq));
    }

    return rows;
  }, [q, statusFilter, subscribers]);

  const handleDemoClick = () => {
    return isDemoMode;
  };

  const handleToggleImported = async (subscriber) => {
    if (handleDemoClick()) return;

    const nextValue = !subscriber.is_imported_to_contacts;

    setActionId(`toggle-${subscriber.id}`);

    try {
      const token = getToken();

      const res = await toggleNewsletterSubscriberImported(
        subscriber.id,
        nextValue,
        token
      );

      const updated = res?.subscriber;

      setSubscribers((prev) =>
        prev.map((item) =>
          item.id === subscriber.id
            ? {
                ...item,
                is_imported_to_contacts:
                  updated?.is_imported_to_contacts ?? nextValue,
                imported_at:
                  updated?.imported_at ?? (nextValue ? new Date().toISOString() : null),
              }
            : item
        )
      );
    } catch (err) {
      console.error("Erreur mise à jour email newsletter :", err);
      setError("Impossible de mettre à jour cet email.");
    } finally {
      setActionId("");
    }
  };

  const handleDelete = async (subscriber) => {
    if (handleDemoClick()) return;

    const confirmed = window.confirm(
      `Supprimer définitivement l'adresse ${subscriber.email} ?`
    );

    if (!confirmed) return;

    setActionId(`delete-${subscriber.id}`);

    try {
      const token = getToken();

      await deleteNewsletterSubscriber(subscriber.id, token);

      setSubscribers((prev) =>
        prev.filter((item) => item.id !== subscriber.id)
      );
    } catch (err) {
      console.error("Erreur suppression email newsletter :", err);
      setError("Impossible de supprimer cet email.");
    } finally {
      setActionId("");
    }
  };

  const handleCopy = async (subscriber) => {
    try {
      await navigator.clipboard.writeText(subscriber.email);
      setCopiedId(subscriber.id);

      window.setTimeout(() => {
        setCopiedId(null);
      }, 1400);
    } catch {
      // Clipboard non disponible : pas bloquant.
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 md:p-5 shadow-sm font-montserrat">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <FiUserPlus className="h-4 w-4" />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Emails pour informations communales
              </h3>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                {stats.total} email(s) —{" "}
                <span className="font-semibold">{stats.pending} à intégrer</span>{" "}
                —{" "}
                <span className="font-semibold">{stats.imported} intégré(s)</span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            {isDemoMode && (
              <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold">
                Mode démonstration
              </span>
            )}

            <button
              type="button"
              onClick={loadSubscribers}
              disabled={loading}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FiRefreshCw
                className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"}
              />
              Actualiser
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher une adresse email..."
              className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-xs font-medium text-slate-700 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setStatusFilter("pending")}
              className={
                "h-9 rounded-lg px-3 text-xs font-semibold transition " +
                (statusFilter === "pending"
                  ? "bg-emerald-600 text-white"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800")
              }
            >
              À intégrer ({stats.pending})
            </button>

            <button
              type="button"
              onClick={() => setStatusFilter("imported")}
              className={
                "h-9 rounded-lg px-3 text-xs font-semibold transition " +
                (statusFilter === "imported"
                  ? "bg-sky-600 text-white"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800")
              }
            >
              Intégrés ({stats.imported})
            </button>

            <button
              type="button"
              onClick={() => setStatusFilter("all")}
              className={
                "h-9 rounded-lg px-3 text-xs font-semibold transition " +
                (statusFilter === "all"
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800")
              }
            >
              Tous ({stats.total})
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="h-12 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800"
            />
          ))}
        </div>
      ) : filteredSubscribers.length > 0 ? (
        <div className="overflow-auto max-h-[32rem]">
          <table className="min-w-full text-sm bg-white dark:bg-slate-900">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-200">
                  Date
                </th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-200">
                  Email
                </th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-200">
                  Statut
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredSubscribers.map((subscriber) => {
                const imported = !!subscriber.is_imported_to_contacts;
                const toggleLoading = actionId === `toggle-${subscriber.id}`;
                const deleteLoading = actionId === `delete-${subscriber.id}`;

                return (
                  <tr
                    key={subscriber.id}
                    className={
                      imported
                        ? "bg-slate-50 dark:bg-slate-800/60"
                        : "bg-white dark:bg-slate-900"
                    }
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700 dark:text-slate-100 align-top">
                      <div>{formatDate(subscriber.registered_at)}</div>
                      <div className="mt-0.5 text-[11px] text-slate-400">
                        {formatDateTime(subscriber.registered_at)}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top text-xs text-slate-700 dark:text-slate-200">
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300">
                          <FiMail className="h-3.5 w-3.5" />
                        </span>

                        <div className="min-w-0">
                          <a
                            href={`mailto:${subscriber.email}`}
                            className="font-semibold text-slate-900 hover:text-sky-600 hover:underline dark:text-slate-50 dark:hover:text-sky-300 break-all"
                          >
                            {subscriber.email}
                          </a>

                          {imported && subscriber.imported_at && (
                            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                              Intégré le {formatDateTime(subscriber.imported_at)}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {imported ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                          <FiCheckCircle className="h-3.5 w-3.5" />
                          Dans la base
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                          <FiXCircle className="h-3.5 w-3.5" />
                          À saisir
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-right align-top">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleCopy(subscriber)}
                          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <FiClipboard className="h-3.5 w-3.5" />
                          {copiedId === subscriber.id ? "Copié" : "Copier"}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleToggleImported(subscriber)}
                          disabled={toggleLoading || isDemoMode}
                          className={
                            "inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-semibold transition disabled:opacity-60 " +
                            (imported
                              ? "border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                              : "bg-emerald-600 text-white hover:bg-emerald-700")
                          }
                        >
                          {toggleLoading ? (
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <FiCheckCircle className="h-3.5 w-3.5" />
                          )}
                          {imported ? "Annuler" : "Rentré"}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(subscriber)}
                          disabled={deleteLoading || isDemoMode}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100 disabled:opacity-60 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300"
                          title="Supprimer"
                        >
                          {deleteLoading ? (
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <FiTrash2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-slate-500 text-center py-6">
          Aucun email ne correspond aux critères sélectionnés.
        </p>
      )}
    </div>
  );
}